import { z } from "zod";
const bounded = (maximum) => z.string().trim().min(1).max(maximum);
const capabilityIdSchema = z.string().regex(/^(?:channel|operation|plugin|product|skill):[a-z0-9][a-z0-9_.:-]{0,238}$/u);
const unique = (values) => new Set(values).size === values.length;
const sortedUniqueText = (maximumItems, maximumLength) => z.array(bounded(maximumLength)).max(maximumItems).refine(unique, "Values must be unique.")
    .transform((values) => [...values].sort());
/** Compact routing metadata. Execution authority remains in existing typed contracts. */
export const v2HybridCapabilityCardSchema = z.object({
    id: capabilityIdSchema,
    kind: z.enum(["product", "warehouse", "plugin_tool", "operation", "skill", "channel"]),
    title: bounded(240),
    description: bounded(2_000),
    aliases: sortedUniqueText(32, 240),
    activationPhrases: sortedUniqueText(64, 500),
    /** Supporting relevance only; never an exact activation or standalone match. */
    supportingPhrases: sortedUniqueText(64, 500).optional(),
    useWhen: sortedUniqueText(64, 1_000),
    doNotUseWhen: sortedUniqueText(64, 1_000),
    access: z.enum(["read", "write"]),
    sideEffect: z.boolean(),
    dependencies: z.array(capabilityIdSchema).max(64).refine(unique, "Dependencies must be unique.")
        .transform((values) => [...values].sort()),
}).strict().superRefine((card, context) => {
    if (card.sideEffect !== (card.access === "write")) {
        context.addIssue({
            code: "custom",
            path: ["sideEffect"],
            message: "Write access and side-effect routing metadata must agree.",
        });
    }
    if (card.dependencies.includes(card.id)) {
        context.addIssue({
            code: "custom",
            path: ["dependencies"],
            message: "A capability cannot depend on itself.",
        });
    }
});
export const v2HybridCapabilitySelectionModeSchema = z.enum([
    "explicit_pin",
    "exact_alias",
    "exact_phrase",
    "semantic",
    "mixed",
]);
/**
 * A workspace channel rendered as routing metadata. The channel id keeps the
 * card bound to the record it describes; the card itself is the routing text.
 */
export const v2HybridChannelCardSchema = z.object({
    channelId: bounded(200),
    card: v2HybridCapabilityCardSchema,
}).strict();
/**
 * Persisted channel routing evidence for one turn. The worker claim rebuilds
 * the identical capability catalogue from these cards rather than from live
 * channel rows, so channel edits between preparation and claim cannot change
 * the catalogue hash.
 */
export const v2HybridChannelRoutingSchema = z.object({
    eligibleChannelCards: z.array(v2HybridChannelCardSchema).max(500),
    exposedChannelIds: z.array(bounded(200)).max(500),
    workspaceReadFallback: z.boolean(),
}).strict();
